import { useToggle } from "@uidotdev/usehooks";
import { cn } from "cn";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { BwCheckmarkLine, BwChevronDownLine } from "@/components/ui/icons";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { OrderTypeIcon } from "@/components/ui/status/order-type-icon";
import { Typography } from "@/components/ui/typography";
import { formatEnumLabel } from "@/libs/format-enum-label";
import { OrderType } from "@/types/openapi-enum";

type TypeSelectProps = {
  className?: string;
  value: OrderType;
  onChange: (value: OrderType) => void;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  modal?: boolean;
  disabled?: boolean;
};

const typeOptions = [OrderType.Project, OrderType.Service, OrderType.Warranty];

export default function TypeSelect({
  className,
  value,
  onChange,
  defaultOpen = false,
  onOpenChange,
  modal = true,
  disabled,
  ...props
}: TypeSelectProps) {
  const [open, toggleOpen] = useToggle(defaultOpen);

  const handleOpenChange = (open: boolean) => {
    if (disabled) {
      return;
    }
    onOpenChange?.(open);
    toggleOpen(open);
  };

  const formatTypeValue = (value: OrderType) => {
    return formatEnumLabel(value);
  };

  return (
    <Popover modal={modal} open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger disabled={disabled} className="group" asChild>
        <div
          data-test="type-select-trigger"
          role="combobox"
          className={cn(
            /* sorted */
            "flex items-center justify-between",
            "w-full min-h-10",
            "px-3 py-2",
            "cursor-pointer rounded-lg",
            "aria-invalid:outline-outline-negative-max data-[state=open]:outline-outline-dark-max data-[state=open]:outline-[1.5px] group-disabled:cursor-not-allowed group-disabled:bg-bg-dark-low group-disabled:opacity-50",
            !value && "text-text-dark-min",
            className,
          )}
          {...props}
        >
          {value ? (
            <Typography
              className="flex items-center gap-2 text-text-dark w-max"
              size="sm"
            >
              {formatTypeValue(value)}
            </Typography>
          ) : (
            <Typography size="sm" className="text-text-dark-min">
              Select type
            </Typography>
          )}

          <BwChevronDownLine
            size={16}
            className="text-text-dark-high shrink-0"
          />
        </div>
      </PopoverTrigger>
      <PopoverContent className="p-0">
        <Command shouldFilter={false} tabIndex={0}>
          <CommandList className="outline-none">
            <CommandEmpty>No type found.</CommandEmpty>
            <CommandGroup>
              {typeOptions.map((type: any) => (
                <CommandItem
                  value={type}
                  key={type}
                  onSelect={() => {
                    // Set value to form field of the parent
                    onChange(type);
                    handleOpenChange(false);
                  }}
                >
                  <div className="flex items-center gap-2 w-full">
                    <OrderTypeIcon type={type} />
                    <Typography
                      className="flex items-center gap-2 text-text-dark grow"
                      size="sm"
                    >
                      {formatTypeValue(type)}
                    </Typography>

                    <BwCheckmarkLine
                      size={16}
                      className={cn(
                        /* sorted */
                        "shrink-0",
                        value === type ? "opacity-100" : "opacity-0",
                      )}
                    />
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
